import { useState, useEffect, useCallback, useMemo } from "react";
import Api from "../../Auth/Api";
import { formatDate, truncateString } from "../../Components/jwtUtils";
import { Link } from "react-router-dom";
import { useAjustes } from '../../Context/AjustesContext';

function DashboardAluno() {

    const token = JSON.parse(localStorage.getItem("user_token"));
    const nome_completo = localStorage.getItem("nome_completo");
    const { ajustes } = useAjustes();

    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) {
            return path; // Already a full URL (external like Imgur)
        }
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);

    const [dataCursos, setDataCursos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-cursos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataCursos(response.data);
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token]);

    const [dataNotificacoes, setDataNotificacoes] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/notificacoes`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataNotificacoes(response.data);
            } catch (error) {
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    const [estatisticas, setEstatisticas] = useState(null);
    const [loadingEstatisticas, setLoadingEstatisticas] = useState(true);
    useEffect(() => {
        async function fetchEstatisticas() {
            try {
                setLoadingEstatisticas(true);
                const response = await Api.get(`/api/estatisticas-aluno`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setEstatisticas(response.data);
            } catch (error) {
                console.error("Erro ao carregar estatísticas:", error);
                setEstatisticas({
                    total_aulas_concluidas: 0,
                    total_certificados: 0,
                    total_modulos_concluidos: 0,
                    cursos_em_andamento: 0,
                    cursos_finalizados: 0,
                    total_cursos: 0
                });
            } finally {
                setLoadingEstatisticas(false);
            }
        }
        fetchEstatisticas();
    }, [token]);

    // /api/notificacoes mistura tipos com formatos diferentes; cada um vira { key, to, titulo, subtitulo }
    const atividadesPendentes = useMemo(() => {
        const lista = dataNotificacoes.notificacoes || [];
        const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
        const cursosComReciclagem = new Set();
        const itens = [];

        lista.forEach((n) => {
            const key = `${n.tipo}-${n.id}`;
            if (n.tipo === 'avaliacao_extra') {
                itens.push({ key, to: `/aulas/${n.id_modulo}`, titulo: n.nome_avaliacao, subtitulo: n.nome_modulo });
            } else if (n.tipo === 'reciclagem_certificado') {
                // liberação + lembretes do mesmo curso: um card só (a API traz o mais recente primeiro)
                if (cursosComReciclagem.has(n.idCurso)) return;
                cursosComReciclagem.add(n.idCurso);
                itens.push({
                    key,
                    to: `/modulos/${n.idCurso}`,
                    titulo: 'Reciclagem de certificado',
                    subtitulo: `${n.nome_curso} - certificado válido até ${dataBr(n.data_expiracao)}`
                });
            } else if (n.tipo === 'reinicializacao_forcada') {
                itens.push({ key, to: `/modulos/${n.idCurso}`, titulo: 'Curso reiniciado', subtitulo: n.nome_curso });
            } else if (n.tipo_notificacao === 'agendamento_confirmacao') {
                itens.push({ key, to: '/agendamentos-treinamentos', titulo: 'Agendamento confirmado', subtitulo: n.nome_curso });
            }
        });
        return itens;
    }, [dataNotificacoes]);

    const hasNotificacoes = atividadesPendentes.length > 0;
    const cursosColClass = hasNotificacoes ? 'col-md-8' : 'col-md-12';

    return (
        <div className="fade-in w-100 mb-3">
            <div className="row">
                <div className="col-12 mb-5 mb-md-0">
                    {/* Card Flag - Desktop */}
                    <div className="card card-flag-aluno rounded-5 w-100 d-none d-md-block overflow-hidden mb-4 border-none">
                        <div className="position-absolute w-100 h-100" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}></div>
                        <img 
                            src={ajustes.flag ? getImageUrl(ajustes.flag) : "./../assets/flag.png"} 
                            className="card-img w-100 card-img card-img-banner" 
                            alt="OK" 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "./../assets/flag.png";
                            }}
                        />
                        <div className="card-img-overlay d-flex flex-column justify-content-center">
                            <div className="text-white fs-1 fw-bold text-center">
                                Seja bem vindo!
                                <p className="fs-6 fw-normal">{nome_completo}</p>
                            </div>
                        </div>
                    </div>

                    {/* Card Flag - Mobile */}
                    <div className="card card-flag-aluno-mobile rounded-3 w-100 d-md-none overflow-hidden mb-3 border-none">
                        <div className="position-absolute w-100 h-100" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}></div>
                        <img 
                            src={ajustes.flag ? getImageUrl(ajustes.flag) : "./../assets/flag.png"} 
                            className="card-img w-100" 
                            style={{ height: '120px', objectFit: 'cover' }}
                            alt="OK" 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "./../assets/flag.png";
                            }}
                        />
                        <div className="card-img-overlay d-flex flex-column justify-content-center">
                            <div className="text-white fs-5 fw-bold text-center">
                                Seja bem vindo!
                                <p className="fs-7 fw-normal mb-0">{nome_completo}</p>
                            </div>
                        </div>
                    </div>

                    {/* Cards de Estatísticas - Desktop */}
                    {loadingEstatisticas ? (
                        <div className="row g-3 mb-4 d-none d-md-flex">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="col-md-6 col-lg-3">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-6 placeholder-lg"></span>
                                                </div>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-4 placeholder-lg"></span>
                                                </div>
                                            </div>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-8"></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : estatisticas && (
                        <div className="row g-3 mb-5 d-none d-md-flex estatisticas-cards">
                            {/* Aulas Concluídas */}
                            <div className="col-md-6 col-lg-3">
                                <div className="card border-0 shadow-sm h-100 bg-white estatistica-card">
                                    <div className="card-body p-0 d-flex align-items-stretch">
                                        <div className="estatistica-icon-box bg-success d-flex align-items-center justify-content-center">
                                            <i className="bi bi-check-circle text-white"></i>
                                        </div>
                                        <div className="flex-grow-1 d-flex flex-column justify-content-center p-3 p-xl-4">
                                            <div className="text-muted small mb-1 estatistica-label">Aulas Concluídas</div>
                                            <div className="fw-bold estatistica-numero text-dark">{estatisticas.total_aulas_concluidas}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Certificados */}
                            <div className="col-md-6 col-lg-3">
                                <div className="card border-0 shadow-sm h-100 bg-white estatistica-card">
                                    <div className="card-body p-0 d-flex align-items-stretch">
                                        <div className="estatistica-icon-box bg-primary d-flex align-items-center justify-content-center">
                                            <i className="bi bi-award text-white"></i>
                                        </div>
                                        <div className="flex-grow-1 d-flex flex-column justify-content-center p-3 p-xl-4">
                                            <div className="text-muted small mb-1 estatistica-label">Certificados</div>
                                            <div className="fw-bold estatistica-numero text-dark">{estatisticas.total_certificados}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Cursos em Andamento */}
                            <div className="col-md-6 col-lg-3">
                                <div className="card border-0 shadow-sm h-100 bg-white estatistica-card">
                                    <div className="card-body p-0 d-flex align-items-stretch">
                                        <div className="estatistica-icon-box bg-warning d-flex align-items-center justify-content-center">
                                            <i className="bi bi-arrow-repeat text-white"></i>
                                        </div>
                                        <div className="flex-grow-1 d-flex flex-column justify-content-center p-3 p-xl-4">
                                            <div className="text-muted small mb-1 estatistica-label">Em Andamento</div>
                                            <div className="fw-bold estatistica-numero text-dark">{estatisticas.cursos_em_andamento}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Cursos Finalizados */}
                            <div className="col-md-6 col-lg-3">
                                <div className="card border-0 shadow-sm h-100 bg-white estatistica-card">
                                    <div className="card-body p-0 d-flex align-items-stretch">
                                        <div className="estatistica-icon-box bg-success d-flex align-items-center justify-content-center">
                                            <i className="bi bi-check2-all text-white"></i>
                                        </div>
                                        <div className="flex-grow-1 d-flex flex-column justify-content-center p-3 p-xl-4">
                                            <div className="text-muted small mb-1 estatistica-label">Finalizados</div>
                                            <div className="fw-bold estatistica-numero text-dark">{estatisticas.cursos_finalizados}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                    <div className="mt-4 mb-4">
                        <h2 className="text-primary fs-2 fw-bold mb-0">Meus Cursos</h2>
                        <p className="text-muted mb-0">Continue seus estudos e acompanhe seu progresso</p>
                    </div>
                    
                    {/* Container Desktop */}
                    <div className="d-none d-md-block">
                        <div className="row g-4">
                            {/* Lado esquerdo - Grid de Cursos */}
                            <div className={`${cursosColClass} col-12`}>
                                {dataCursos && dataCursos.length > 0 ? (
                                    <div className="row g-4">
                                        {dataCursos.map((value) => {
                                            const hoje = new Date();
                                            const dataInicio = new Date(value.dataInicio);
                                            const dataFinal = new Date(value.dataFinal);
                                            const aindaNaoIniciou = hoje < dataInicio;
                                            const jaExpirou = hoje > dataFinal;
                                            // Curso está disponível se: tem permissão E está dentro do período
                                            const isDisponivel = value.disponivel === 1 && !aindaNaoIniciou && !jaExpirou;
                                            
                                            const cardContent = (
                                                <div 
                                                    className={`card border-0 shadow-sm h-100 ${isDisponivel ? 'hover-lift' : 'opacity-75'}`} 
                                                    style={{ 
                                                        transition: 'all 0.3s ease', 
                                                        cursor: isDisponivel ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    {/* Imagem do Curso */}
                                                    <div style={{ position: 'relative', width: '100%', height: '180px', overflow: 'hidden' }}>
                                                        <img 
                                                            src={`${process.env.REACT_APP_IMG_URL}/${value.thumbnail}`} 
                                                            alt={value.nome}
                                                            style={{ 
                                                                width: '100%', 
                                                                height: '100%', 
                                                                objectFit: 'cover',
                                                                filter: isDisponivel ? 'none' : 'grayscale(50%)'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.src = './../assets/flag.png';
                                                            }}
                                                        />
                                                        {/* Badge de Status */}
                                                        {!isDisponivel && (
                                                            <div style={{ 
                                                                position: 'absolute', 
                                                                top: '0.75rem', 
                                                                left: '0.75rem',
                                                                zIndex: 1
                                                            }}>
                                                                <span className="badge bg-secondary">
                                                                    {aindaNaoIniciou ? 'Em breve' : jaExpirou ? 'Expirado' : 'Indisponível'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {/* Badge de Progresso */}
                                                        {isDisponivel && value.porcentagem_concluida && parseInt(value.porcentagem_concluida) > 0 && parseInt(value.porcentagem_concluida) <= 100 && (
                                                            <div style={{ 
                                                                position: 'absolute', 
                                                                top: '0.75rem', 
                                                                right: '0.75rem',
                                                                zIndex: 1
                                                            }}>
                                                                <span className={`badge ${parseInt(value.porcentagem_concluida) === 100 ? 'bg-success' : 'bg-primary'}`}>
                                                                    {parseInt(value.porcentagem_concluida)}%
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Conteúdo do Card */}
                                                    <div className="card-body d-flex flex-column">
                                                        <h5 className="card-title fw-bold mb-2 text-dark" style={{ minHeight: '3rem' }}>
                                                            {value.nome}
                                                        </h5>
                                                        <p className="card-text text-muted small mb-3 flex-grow-1" style={{ 
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}>
                                                            {truncateString(value.resumo, 120)}
                                                        </p>
                                                        
                                                        {/* Barra de Progresso */}
                                                        {(() => {
                                                            const porcentagem = parseInt(value.porcentagem_concluida);
                                                            return isDisponivel && !isNaN(porcentagem) && porcentagem > 0 && porcentagem <= 100;
                                                        })() && (
                                                            <div className="mb-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                                    <small className="text-muted">Progresso</small>
                                                                    <small className="text-muted fw-medium">{parseInt(value.porcentagem_concluida)}%</small>
                                                                </div>
                                                                <div className="progress" style={{ height: '6px', borderRadius: '10px' }}>
                                                                    <div 
                                                                        className={`progress-bar ${parseInt(value.porcentagem_concluida) === 100 ? 'bg-success' : ''}`}
                                                                        role="progressbar" 
                                                                        style={{ 
                                                                            width: `${parseInt(value.porcentagem_concluida)}%`,
                                                                            transition: 'width 0.3s ease'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Footer do Card */}
                                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                                            <small className="text-muted">
                                                                <i className="bi bi-calendar3 me-1"></i>
                                                                {aindaNaoIniciou ? `Inicia em ${formatDate(value.dataInicio)}` : 
                                                                 jaExpirou ? `Expirou em ${formatDate(value.dataFinal)}` : 
                                                                 `Até ${formatDate(value.dataFinal)}`}
                                                            </small>
                                                            {isDisponivel ? (
                                                                <span className="btn btn-primary btn-sm">
                                                                    Acessar <i className="bi bi-arrow-right ms-1"></i>
                                                                </span>
                                                            ) : (
                                                                <span className="btn btn-secondary btn-sm disabled" style={{ cursor: 'not-allowed' }}>
                                                                    Indisponível
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );

                                            return (
                                                <div key={value.id} className="col-md-6 col-lg-4">
                                                    {isDisponivel ? (
                                                        <Link 
                                                            to={`/modulos/${value.id}`}
                                                            className="text-decoration-none"
                                                            style={{ color: 'inherit' }}
                                                        >
                                                            {cardContent}
                                                        </Link>
                                                    ) : (
                                                        <div style={{ pointerEvents: 'none' }}>
                                                            {cardContent}
                                                        </div>
                                                    )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-book text-muted" style={{ fontSize: '3rem' }}></i>
                                        <p className="text-muted mt-3">Nenhum curso disponível no momento</p>
                                </div>
                                )}
                            </div>

                            {/* Lado direito - Atividades Pendentes */}
                            {hasNotificacoes && (
                                <div className="col-md-4 col-12">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-primary text-white">
                                            <h5 className="mb-0 d-flex align-items-center">
                                                <i className="bi bi-bell-fill me-2"></i>
                                                Atividades Pendentes
                                            </h5>
                                        </div>
                                        <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                            {atividadesPendentes.map((value) => (
                                                <Link 
                                                    key={value.key} 
                                                    to={value.to}
                                                    className="text-decoration-none"
                                                >
                                                    <div className="card border mb-3 hover-lift" style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}>
                                                        <div className="card-body p-3">
                                                            <h6 className="card-title text-dark mb-2 fw-semibold">
                                                                {value.titulo}
                                                            </h6>
                                                            <p className="card-text text-muted small mb-2">
                                                                <i className="bi bi-book me-1"></i>
                                                                {value.subtitulo}
                                                            </p>
                                                            <div className="d-flex justify-content-end">
                                                                <span className="text-primary small fw-medium">
                                                                    Acessar <i className="bi bi-arrow-right ms-1"></i>
                                                                </span>
                                                            </div>
                                                        </div>
                                                </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Container Mobile */}
                    <div className="d-md-none">
                        {/* Cards de Estatísticas - Mobile */}
                        {loadingEstatisticas ? (
                            <div className="row g-2 mb-3">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="col-6">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-body p-2">
                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                    <div className="placeholder-glow">
                                                        <span className="placeholder col-4 placeholder-lg"></span>
                                                    </div>
                                                    <div className="placeholder-glow">
                                                        <span className="placeholder col-3 placeholder-lg"></span>
                                                    </div>
                                                </div>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : estatisticas && (
                            <div className="row g-2 mb-3">
                                {/* Aulas Concluídas */}
                                <div className="col-6">
                                    <div className="card border-0 shadow-sm bg-white">
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div>
                                                    <div className="fs-4 fw-bold text-success">{estatisticas.total_aulas_concluidas}</div>
                                                    <div className="text-muted small">Aulas Concluídas</div>
                                                </div>
                                                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.2rem' }}></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Certificados */}
                                <div className="col-6">
                                    <div className="card border-0 shadow-sm bg-white">
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div>
                                                    <div className="fs-4 fw-bold text-primary">{estatisticas.total_certificados}</div>
                                                    <div className="text-muted small">Certificados</div>
                                                </div>
                                                <i className="bi bi-award-fill text-primary" style={{ fontSize: '1.2rem' }}></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Cursos em Andamento */}
                                <div className="col-6">
                                    <div className="card border-0 shadow-sm bg-white">
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div>
                                                    <div className="fs-4 fw-bold text-warning">{estatisticas.cursos_em_andamento}</div>
                                                    <div className="text-muted small">Em Andamento</div>
                                                </div>
                                                <i className="bi bi-arrow-repeat text-warning" style={{ fontSize: '1.2rem' }}></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Cursos Finalizados */}
                                <div className="col-6">
                                    <div className="card border-0 shadow-sm bg-white">
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div>
                                                    <div className="fs-4 fw-bold text-success">{estatisticas.cursos_finalizados}</div>
                                                    <div className="text-muted small">Finalizados</div>
                                                </div>
                                                <i className="bi bi-check2-all text-success" style={{ fontSize: '1.2rem' }}></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Atividades pendentes - Mobile primeiro */}
                        {hasNotificacoes && (
                            <div className="rounded bg-primary text-white p-3 mb-3">
                                <span className="d-flex justify-content-center fs-6 fw-500 text-center mb-3">Atividades pendentes</span>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {atividadesPendentes.map((value) => (
                                        <div key={value.key} className="d-flex flex-column rounded bg-white text-dark p-2 mb-2">
                                            <span className="fw-normal fs-7 text-pretty">{value.titulo}</span>
                                            <span className="fw-normal fs-8 text-pretty text-dark">{value.subtitulo}</span>
                                            <Link className="fw-bold fs-8 text-nowrap cursor-pointer text-end text-dark" to={value.to}>Acessar</Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Cursos - Mobile */}
                        <div className="mb-4">
                            <h5 className="text-primary fs-5 fw-bold mb-3">Meus Cursos</h5>
                            {dataCursos && dataCursos.map((value) => {
                                const hoje = new Date();
                                const dataInicio = new Date(value.dataInicio);
                                const dataFinal = new Date(value.dataFinal);
                                const aindaNaoIniciou = hoje < dataInicio;
                                const jaExpirou = hoje > dataFinal;
                                const isDisponivel = value.disponivel === 1 && !aindaNaoIniciou && !jaExpirou;

                                const cardContent = (
                                    <div className={`border-0 p-2 shadow-sm mb-3 rounded-3 bg-white ${!isDisponivel ? 'opacity-75' : ''}`} style={{ display: 'block', color: 'inherit' }}>
                                    <div className="d-flex flex-column">
                                            <div className="mb-2 text-center position-relative">
                                            <img 
                                                src={`${process.env.REACT_APP_IMG_URL}/${value.thumbnail}`} 
                                                alt="Curso Imagem" 
                                                className="rounded-3" 
                                                    style={{ 
                                                        width: '100%', 
                                                        maxWidth: '200px', 
                                                        height: '120px', 
                                                        objectFit: 'cover',
                                                        filter: isDisponivel ? 'none' : 'grayscale(50%)'
                                                    }}
                                            />
                                                {!isDisponivel && (
                                                    <div style={{ 
                                                        position: 'absolute', 
                                                        top: '0.5rem', 
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        zIndex: 1
                                                    }}>
                                                        <span className="badge bg-secondary">
                                                            {aindaNaoIniciou ? 'Em breve' : jaExpirou ? 'Expirado' : 'Indisponível'}
                                                        </span>
                                                    </div>
                                                )}
                                        </div>
                                        <div>
                                            <h6 className="card-title mb-2 fs-6 fw-bold text-dark">{value.nome}</h6>
                                            <p className="card-text fs-7 mb-2 text-dark">{truncateString(value.resumo, 100)}</p>
                                            <div className="d-flex justify-content-between align-items-center border-top pt-2">
                                                    <p className="card-text fs-8 text-muted mb-0">
                                                        {aindaNaoIniciou ? `Inicia em ${formatDate(value.dataInicio)}` : 
                                                         jaExpirou ? `Expirou em ${formatDate(value.dataFinal)}` : 
                                                         `Até ${formatDate(value.dataFinal)}`}
                                                    </p>
                                                    {isDisponivel ? (
                                                <span className="btn btn-primary btn-sm">Acessar</span>
                                                    ) : (
                                                        <span className="btn btn-secondary btn-sm disabled" style={{ cursor: 'not-allowed' }}>
                                                            Indisponível
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );

                                return (
                                    <div key={value.id}>
                                        {isDisponivel ? (
                                            <Link 
                                                to={`/modulos/${value.id}`}
                                                className="text-decoration-none"
                                                style={{ color: 'inherit' }}
                                            >
                                                {cardContent}
                                </Link>
                                        ) : (
                                            <div style={{ pointerEvents: 'none' }}>
                                                {cardContent}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>

            </div>
        </div >
    )
}

export default DashboardAluno;